import React, { useEffect, useState } from "react";
import { Banknote, Settings2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { ugx, when } from "./shared";

const KIND = {
  RELEASE: { label: "Refill released", tone: "text-red-600" },
  RETURN: { label: "Empty returned", tone: "text-emerald-600" },
  REPAYMENT: { label: "Repayment to Stabex", tone: "text-emerald-600" },
  REVERSAL: { label: "Order cancelled", tone: "text-slate-600" },
};
const signed = (n) => (n ? `${n > 0 ? "+" : "−"}${ugx(Math.abs(n))}` : "—");

const MoneyDialog = ({ title, desc, fields, open, onOpenChange, onSubmit, testId }) => {
  const [vals, setVals] = useState({});
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (open) setVals(Object.fromEntries(fields.map((f) => [f.key, f.initial ?? ""]))); }, [open]); // eslint-disable-line
  const submit = async (e) => {
    e.preventDefault(); setSaving(true);
    try { await onSubmit(vals); onOpenChange(false); } catch (err) { toast.error(formatDetail(err.response?.data?.detail) || "Save failed"); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-sm max-w-sm" data-testid={testId}>
        <DialogHeader><DialogTitle className="font-heading">{title}</DialogTitle><DialogDescription>{desc}</DialogDescription></DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          {fields.map((f) => (
            <div key={f.key}><Label>{f.label}</Label>
              <Input type={f.type || "text"} value={vals[f.key] ?? ""} onChange={(e) => setVals({ ...vals, [f.key]: e.target.value })} className="rounded-sm mt-1" data-testid={`${testId}-${f.key}`} /></div>
          ))}
          <DialogFooter><Button type="submit" disabled={saving} className="rounded-sm bg-brand-600 hover:bg-brand-700" data-testid={`${testId}-save`}>{saving ? "Saving…" : "Save"}</Button></DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export const LedgerPanel = ({ facility, onChanged, isAdmin }) => {
  const [rows, setRows] = useState([]);
  const [payOpen, setPayOpen] = useState(false);
  const [cfgOpen, setCfgOpen] = useState(false);
  const load = () => api.get("/supply/ledger").then(({ data }) => setRows(data.data || [])).catch(() => {});
  useEffect(() => { load(); }, [facility]); // eslint-disable-line

  const pay = async (v) => {
    await api.post("/supply/repayments", { amount: Number(v.amount), reference: v.reference, notes: v.notes });
    toast.success("Repayment recorded — credit restored"); onChanged();
  };
  const saveCfg = async (v) => {
    await api.put("/supply/facility", { credit_limit: Number(v.credit_limit), guarantee_face_value: Number(v.guarantee_face_value) });
    toast.success("Facility limits saved"); onChanged();
  };

  return (
    <div className="space-y-4" data-testid="ledger-panel">
      {isAdmin && (
        <div className="flex justify-end gap-2">
          <Button size="sm" variant="outline" className="rounded-sm" onClick={() => setCfgOpen(true)} data-testid="facility-edit-btn"><Settings2 size={14} className="mr-1" />Credit &amp; guarantee limits</Button>
          <Button size="sm" className="rounded-sm bg-brand-600 hover:bg-brand-700" onClick={() => setPayOpen(true)} disabled={!facility?.credit_used} data-testid="repayment-btn"><Banknote size={14} className="mr-1" />Record repayment</Button>
        </div>
      )}
      <div className="bg-white border border-slate-200 rounded-sm overflow-x-auto">
        <table className="w-full text-sm" data-testid="ledger-table">
          <thead className="bg-slate-50 text-[10px] uppercase tracking-widest text-slate-500">
            <tr>{["When", "Entry", "Order / reference", "Credit", "Guarantee cover", "By"].map((h) => <th key={h} className="text-left px-4 py-2">{h}</th>)}</tr>
          </thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={6} className="p-8 text-center text-slate-400">No movements yet.</td></tr>}
            {rows.map((r, i) => (
              <tr key={r.id} className="border-t border-slate-100" data-testid={`ledger-row-${i}`}>
                <td className="px-4 py-2 text-[11px] text-slate-500 whitespace-nowrap">{when(r.at)}</td>
                <td className={`px-4 py-2 text-xs font-medium ${KIND[r.kind]?.tone}`}>{KIND[r.kind]?.label || r.kind}</td>
                <td className="px-4 py-2 text-xs font-mono text-slate-700">{r.order_number || r.reference || "—"}</td>
                <td className="px-4 py-2 text-xs tabular-nums">{signed(r.credit_delta)}</td>
                <td className="px-4 py-2 text-xs tabular-nums">{signed(r.cover_delta)}</td>
                <td className="px-4 py-2 text-xs text-slate-600">{r.by?.name || "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <MoneyDialog open={payOpen} onOpenChange={setPayOpen} onSubmit={pay} testId="repayment-dialog" title="Record repayment to Stabex"
        desc={`Outstanding: ${ugx(facility?.credit_used)}`}
        fields={[{ key: "amount", label: "Amount (UGX)", type: "number", initial: facility?.credit_used }, { key: "reference", label: "Payment reference" }, { key: "notes", label: "Notes" }]} />
      <MoneyDialog open={cfgOpen} onOpenChange={setCfgOpen} onSubmit={saveCfg} testId="facility-dialog" title="Credit & guarantee limits"
        desc="One credit line and one bank guarantee cover all Stabex stations."
        fields={[{ key: "credit_limit", label: "LPG credit limit (UGX)", type: "number", initial: facility?.credit_limit },
          { key: "guarantee_face_value", label: "Bank guarantee face value (UGX)", type: "number", initial: facility?.guarantee_face_value }]} />
    </div>
  );
};

export const ReconciliationsPanel = ({ stations }) => {
  const [rows, setRows] = useState([]);
  const [sid, setSid] = useState("");
  useEffect(() => { api.get("/reconciliations", { params: { station_id: sid || undefined } }).then(({ data }) => setRows(data.data || [])).catch(() => {}); }, [sid]);
  return (
    <div className="space-y-4" data-testid="reconciliations-panel">
      <select value={sid} onChange={(e) => setSid(e.target.value)} className="h-9 border border-slate-300 rounded-sm px-3 text-sm bg-white" data-testid="recon-station-filter">
        <option value="">All stations</option>
        {stations.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
      </select>
      <div className="bg-white border border-slate-200 rounded-sm overflow-x-auto">
        <table className="w-full text-sm" data-testid="recon-table">
          <thead className="bg-slate-50 text-[10px] uppercase tracking-widest text-slate-500">
            <tr>{["Submitted", "Station", "Counts (full / empty)", "Activity today", "Result", "By"].map((h) => <th key={h} className="text-left px-4 py-2">{h}</th>)}</tr>
          </thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={6} className="p-8 text-center text-slate-400">No end-of-day counts submitted yet.</td></tr>}
            {rows.map((r, i) => (
              <tr key={r.id} className="border-t border-slate-100 align-top" data-testid={`recon-row-${i}`}>
                <td className="px-4 py-2 text-[11px] text-slate-500 whitespace-nowrap">{when(r.at)}</td>
                <td className="px-4 py-2 text-xs text-slate-800">{r.station_name}</td>
                <td className="px-4 py-2 text-xs tabular-nums space-y-0.5">
                  {r.rows.map((x) => (
                    <div key={x.cylinder_type_id}>{x.cylinder_type}: {x.full_counted} / {x.empty_counted}
                      {(x.full_variance || x.empty_variance) ? <span className="text-amber-700"> (Δ {x.full_variance >= 0 ? "+" : ""}{x.full_variance} / {x.empty_variance >= 0 ? "+" : ""}{x.empty_variance})</span> : null}</div>
                  ))}
                </td>
                <td className="px-4 py-2 text-[11px] text-slate-600">{r.released_today} released · {r.empties_returned_today} empties back · {r.walkin_sales_today} walk-in</td>
                <td className="px-4 py-2 text-xs">{r.has_variance ? <span className="text-amber-700 font-semibold">Variance{r.applied ? " · adjusted" : ""}</span> : <span className="text-emerald-700 font-semibold">Matched</span>}</td>
                <td className="px-4 py-2 text-xs text-slate-600">{r.by?.name}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};
